import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, LockIcon, MicIcon } from 'lucide-react';
import ReactPixel from 'react-facebook-pixel';
const BOOK_COVER = 'https://cdn.magicpatterns.com/uploads/gaStatMSyCZvrtwPmZkfsk/heylloyd-image-1772252797058-removebg-preview.png';
const AUDIOBOOK_COVER = 'https://cdn.magicpatterns.com/uploads/vjFx4ZGTGrd8ioeMQCm4G1/heylloyd-image-1772319787085.png';
const RALLY_BG = 'https://cdn.magicpatterns.com/uploads/wRt22iy7F5Yd4bJbPZ8SaR/private_openrouter-generated-image-1772254469664.png';
function SectionReveal({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 40
  }} transition={{
    duration: 0.7,
    ease: 'easeOut'
  }} className={`${`${className} pt-[10px] pb-[10px]`} pt-[0px] pb-[0px]`} data-id="element-2">
      {children}
    </motion.div>;
}
function RedRule({
  full = false
}: {
  full?: boolean;
}) {
  return <div style={{
    width: full ? '100%' : '4rem',
    height: '2px',
    backgroundColor: '#c8102e',
    margin: '1rem 0'
  }} data-id="element-3" />;
}
function SectionLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <p style={{
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 700,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#c8102e',
    fontSize: '0.8rem',
    marginBottom: '0.5rem'
  }} data-id="element-4">
      {children}
    </p>;
}
// Declare fbq on window for TypeScript
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
  }
}
// Pixel ID
const FB_PIXEL_ID = '1518439815912736';
// Safe fbq caller — queues if not yet loaded
function fbq(...args: unknown[]) {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq(...args);
  }
}
export function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  // ===== META PIXEL =====
  useEffect(() => {
    // Don't re-initialize if already done
    if (document.getElementById('fb-pixel-script')) {
      fbq('track', 'PageView');
      return;
    }
    // Build the stub queue BEFORE the script loads
    // so any calls made before load are queued
    if (typeof window.fbq !== 'function') {
      const stub = Object.assign(function (...args: unknown[]) {
        stub.callMethod ? (stub as unknown as {
          callMethod: (...a: unknown[]) => void;
        }).callMethod(...args) : stub.queue.push(args);
      }, {
        queue: [] as unknown[][],
        loaded: true,
        version: '2.0'
      });
      window.fbq = stub as unknown as (...args: unknown[]) => void;
      window._fbq = stub;
    }
    // Inject script — use document.body.appendChild to avoid parentNode crash
    const script = document.createElement('script');
    script.id = 'fb-pixel-script';
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.onload = () => {
      window.fbq('init', FB_PIXEL_ID);
      window.fbq('track', 'PageView');
    };
    document.body.appendChild(script);
  }, []);
  // ===== END META PIXEL =====
  const scrollToPrice = () => {
    priceRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const faqs = [{
    q: 'Why focus on a man without a degree?',
    a: 'Because, as Charlie proved, a degree is a credential, but conviction is a calling. His refusal to attend college was his first great act of protest against a betrayed system—and his greatest lesson to all of us who followed.'
  }, {
    q: 'Is this purely political?',
    a: 'No. It is deeply spiritual. The book explores the Biblical roots that anchored Charlie when the world turned hostile. Liberty, as he argued, is a gift from God—not a grant from the government.'
  }, {
    q: 'Can I share these files?',
    a: 'This license is for a single household of patriots. We encourage the message to spread far and wide, but the legacy must be supported. Share the ideas—support the mission.'
  }];
  // Add modal content definitions before the return statement
  const privacyContent = <div data-id="element-5">
      <p style={{
      fontFamily: "'Oswald', sans-serif",
      fontWeight: 700,
      color: '#c8102e',
      textTransform: 'uppercase',
      letterSpacing: '0.15em',
      fontSize: '0.75rem',
      marginBottom: '0.5rem'
    }} data-id="element-6">
        Last Updated: January 1, 2025
      </p>
      <p style={{
      fontFamily: "'Merriweather', serif",
      color: '#b0b8c8',
      lineHeight: 1.8,
      fontSize: '0.9rem',
      marginBottom: '1.25rem'
    }} data-id="element-7">
        This Privacy Policy describes how Voices of Freedom ("we," "us," or
        "our") collects, uses, and shares information when you visit our website
        and make a purchase.
      </p>
      {[{
      title: '1. Information We Collect',
      body: 'We collect information you provide directly to us when making a purchase, including your name, email address, and payment information. Payment details are processed securely by our third-party payment processor and are never stored on our servers.'
    }, {
      title: '2. How We Use Your Information',
      body: 'We use the information we collect to process your order, deliver your digital products, send you order confirmations and download links, and improve our website and offerings. We do not sell, trade, or rent your personal information to third parties.'
    }, {
      title: '3. Cookies & Tracking',
      body: 'Our website may use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and understand where our visitors are coming from. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.'
    }, {
      title: '4. Third-Party Services',
      body: 'We may use third-party service providers to help us operate our website and process payments. These parties have access to your personal information only to perform specific tasks on our behalf and are obligated not to disclose or use it for any other purpose.'
    }, {
      title: '5. Data Security',
      body: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.'
    }, {
      title: '6. Data Retention',
      body: 'We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.'
    }, {
      title: '7. Your Rights',
      body: 'You have the right to access, correct, or delete your personal information. You may also object to or restrict certain processing of your data. To exercise these rights, please reach out through the appropriate channels listed in our terms.'
    }, {
      title: '8. Changes to This Policy',
      body: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page with an updated effective date. Your continued use of our website after any changes constitutes your acceptance of the new policy.'
    }].map(({
      title,
      body
    }) => <div key={title} style={{
      marginBottom: '1.25rem'
    }} data-id="element-8">
          <h3 style={{
        fontFamily: "'Oswald', sans-serif",
        fontWeight: 700,
        color: '#ffffff',
        textTransform: 'uppercase',
        fontSize: '0.95rem',
        letterSpacing: '0.05em',
        marginBottom: '0.4rem'
      }} data-id="element-9">
            {title}
          </h3>
          <p style={{
        fontFamily: "'Merriweather', serif",
        color: '#b0b8c8',
        lineHeight: 1.8,
        fontSize: '0.875rem',
        margin: 0
      }} data-id="element-10">
            {body}
          </p>
        </div>)}
    </div>;
  const termsContent = <div data-id="element-11">
      <p style={{
      fontFamily: "'Oswald', sans-serif",
      fontWeight: 700,
      color: '#c8102e',
      textTransform: 'uppercase',
      letterSpacing: '0.15em',
      fontSize: '0.75rem',
      marginBottom: '0.5rem'
    }} data-id="element-12">
        Last Updated: January 1, 2025
      </p>
      <p style={{
      fontFamily: "'Merriweather', serif",
      color: '#b0b8c8',
      lineHeight: 1.8,
      fontSize: '0.9rem',
      marginBottom: '1.25rem'
    }} data-id="element-13">
        By purchasing and accessing Voices of Freedom digital products, you
        agree to be bound by these Terms of Service. Please read them carefully
        before completing your purchase.
      </p>
      {[{
      title: '1. Digital Product License',
      body: 'Upon purchase, you are granted a non-exclusive, non-transferable license to access and use the digital products (eBook PDF and Audiobook) for personal, non-commercial use within a single household. You may not reproduce, distribute, publicly display, or create derivative works from any part of the product.'
    }, {
      title: '2. Instant Digital Delivery',
      body: 'All products are delivered digitally via email and/or download link immediately upon successful payment. By completing your purchase, you acknowledge that delivery of digital content begins immediately and you waive any applicable withdrawal rights once the download link has been accessed.'
    }, {
      title: '3. No Refund Policy',
      body: 'Due to the instant digital nature of this product, all sales are final. We do not offer refunds, exchanges, or cancellations once the digital content has been delivered or accessed. Please review all product descriptions carefully before purchasing.'
    }, {
      title: '4. Prohibited Uses',
      body: 'You may not: (a) share, resell, or redistribute the digital files; (b) upload the content to any file-sharing platform; (c) use the content for commercial purposes; (d) remove any copyright or proprietary notices from the materials.'
    }, {
      title: '5. Intellectual Property',
      body: 'All content within the Voices of Freedom digital products, including text, images, and audio, is protected by copyright law. All rights are reserved by Voices of Freedom. Unauthorized use of any content may violate copyright, trademark, and other applicable laws.'
    }, {
      title: '6. Disclaimer of Warranties',
      body: 'The products are provided "as is" without warranty of any kind, express or implied. We do not warrant that the products will meet your specific requirements or that the operation of the products will be uninterrupted or error-free.'
    }, {
      title: '7. Limitation of Liability',
      body: 'In no event shall Voices of Freedom be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the products, even if we have been advised of the possibility of such damages.'
    }, {
      title: '8. Educational Purpose',
      body: 'This product is intended for informational and educational purposes only. The views and opinions expressed within are those of the author and do not necessarily reflect the official policy or position of any organization or entity.'
    }, {
      title: '9. Governing Law',
      body: 'These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the applicable courts.'
    }, {
      title: '10. Modifications',
      body: 'We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the website. Your continued use of our services after any changes constitutes your acceptance of the new Terms.'
    }].map(({
      title,
      body
    }) => <div key={title} style={{
      marginBottom: '1.25rem'
    }} data-id="element-14">
          <h3 style={{
        fontFamily: "'Oswald', sans-serif",
        fontWeight: 700,
        color: '#ffffff',
        textTransform: 'uppercase',
        fontSize: '0.95rem',
        letterSpacing: '0.05em',
        marginBottom: '0.4rem'
      }} data-id="element-15">
            {title}
          </h3>
          <p style={{
        fontFamily: "'Merriweather', serif",
        color: '#b0b8c8',
        lineHeight: 1.8,
        fontSize: '0.875rem',
        margin: 0
      }} data-id="element-16">
            {body}
          </p>
        </div>)}
    </div>;
  const handleBuyClick = () => {
    fbq('track', 'InitiateCheckout');
    window.location.href = 'https://www.mellowreset.com/cart/43035254521922:1';
  };
  return <div style={{
    backgroundColor: '#0d1b2e',
    color: '#ffffff',
    minWidth: '100%'
  }} data-id="element-17">
      {/* ===== HERO SECTION ===== */}
      <section ref={heroRef} style={{
      position: 'relative',
      overflow: 'hidden'
    }} data-id="element-18">
        {/* Background: rally crowd image */}
        <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `url(${RALLY_BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 30%',
        backgroundRepeat: 'no-repeat'
      }} data-id="element-19" />
        {/* Dark overlay */}
        <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to right, rgba(5,10,20,0.95) 0%, rgba(5,10,20,0.82) 50%, rgba(5,10,20,0.65) 100%)'
      }} data-id="element-20" />

        {/* Hero content — always horizontal row */}
        <div style={{
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '1.5rem 1rem 1.5rem 0',
        maxWidth: '1400px',
        margin: '0 auto',
        gap: '0.5rem',
        minHeight: '0'
      }} data-id="element-21">
          {/* COL 1: Book image */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} style={{
          flexShrink: 0
        }} data-id="element-22">
            <img src={BOOK_COVER} alt="Voices of Freedom book" style={{
            width: 'clamp(180px, 32vw, 380px)',
            filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.95)) drop-shadow(0 0 20px rgba(200,16,46,0.4))',
            display: 'block'
          }} data-id="element-23" />
          </motion.div>

          {/* COL 2: Title + red line + subtitle + CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} style={{
          flex: 1,
          minWidth: 0
        }} data-id="element-24">
            <p style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: '#c8102e',
            fontSize: 'clamp(0.6rem, 1.5vw, 0.75rem)',
            marginBottom: '0.3rem'
          }} data-id="element-25">
              ★ New Release ★
            </p>

            <h1 style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 900,
            textTransform: 'uppercase',
            lineHeight: 0.95,
            color: '#ffffff',
            margin: '0 0 0.5rem',
            fontSize: 'clamp(1.6rem, 5.5vw, 4.5rem)',
            letterSpacing: '-0.01em',
            textShadow: '0 2px 15px rgba(0,0,0,0.8)'
          }} data-id="element-26">
              VOICES OF
              <br data-id="element-27" />
              FREEDOM
            </h1>

            {/* Red rule */}
            <motion.div initial={{
            scaleX: 0,
            opacity: 0
          }} animate={{
            scaleX: 1,
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 0.5
          }} style={{
            transformOrigin: 'left',
            height: '3px',
            background: 'linear-gradient(to right, #c8102e, #ff3050)',
            width: '100%',
            margin: '0 0 0.5rem',
            borderRadius: '2px'
          }} data-id="element-28" />

            <h2 style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 700,
            color: '#f0f0f0',
            fontSize: 'clamp(0.75rem, 2.2vw, 1.5rem)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            margin: '0 0 0.25rem'
          }} data-id="element-29">
              The Legacy Of Charlie Kirk
            </h2>

            <p style={{
            fontFamily: "'Merriweather', serif",
            fontStyle: 'italic',
            fontSize: 'clamp(0.7rem, 1.3vw, 0.95rem)',
            margin: '0 0 0.75rem',
            lineHeight: 1.4
          }} className="font-[500] text-[#E0E0E0]" data-id="element-30">
              The Full Story. Unfiltered. Unforgettable.
            </p>

            <button onClick={scrollToPrice} style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            backgroundColor: '#c8102e',
            color: '#ffffff',
            border: 'none',
            padding: 'clamp(0.5rem, 1.5vw, 1rem) clamp(0.75rem, 2vw, 1.75rem)',
            fontSize: 'clamp(0.7rem, 1.8vw, 1rem)',
            cursor: 'pointer',
            boxShadow: '0 4px 0 #7a0a1a, 0 6px 20px rgba(200,16,46,0.5)',
            borderRadius: '4px',
            whiteSpace: 'nowrap'
          }} className="cta-pulse" data-id="element-31">
              GET MY COPY NOW
            </button>
          </motion.div>
        </div>
      </section>

      {/* ===== PRICING SECTION ===== */}

      {/* ===== BUNDLE OFFER BANNER ===== */}
      <div style={{
      background: 'linear-gradient(180deg, #0d1b2e 0%, #111f35 100%)',
      padding: '0.6rem 1rem',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1.5rem'
    }} data-id="element-32">
        <motion.button onClick={scrollToPrice} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} style={{
        background: 'linear-gradient(180deg, #e8152f 0%, #b50d26 100%)',
        color: '#ffffff',
        fontFamily: "'Oswald', sans-serif",
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        fontSize: 'clamp(0.7rem, 1.8vw, 0.95rem)',
        padding: '0.5rem 1.5rem',
        border: '1px solid rgba(255,255,255,0.2)',
        borderBottom: '3px solid #7a0a1a',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        borderRadius: '4px',
        boxShadow: '0 4px 20px rgba(200,16,46,0.4)',
        maxWidth: '500px',
        width: '100%'
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98,
        y: 1
      }} data-id="element-33">
          <span style={{
          fontSize: '0.9rem'
        }} data-id="element-34">
            🔥
          </span>
          <span data-id="element-35">SPECIAL BUNDLE OFFER — LIMITED TIME</span>
          <span style={{
          backgroundColor: 'rgba(0,0,0,0.25)',
          padding: '0.1rem 0.5rem',
          borderRadius: '999px',
          fontSize: '0.65rem',
          letterSpacing: '0.1em',
          border: '1px solid rgba(255,255,255,0.25)',
          whiteSpace: 'nowrap'
        }} data-id="element-36">
            SAVE 76%
          </span>
        </motion.button>

        {/* Product showcase below button */}
        <SectionReveal data-id="element-37">
          <div style={{
          textAlign: 'center'
        }} data-id="element-38">
            <img src={AUDIOBOOK_COVER} alt="Voices of Freedom Audiobook" style={{
            width: '100%',
            maxWidth: '320px',
            margin: '0 auto',
            display: 'block',
            borderRadius: '8px',
            boxShadow: '0 20px 60px rgba(200,16,46,0.3), 0 8px 30px rgba(0,0,0,0.8)'
          }} data-id="element-39" />
            {/* Product labels */}
            <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1.5rem',
            marginTop: '1rem',
            flexWrap: 'wrap'
          }} data-id="element-40">
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }} data-id="element-41">
                <span style={{
                fontSize: '1.1rem'
              }} data-id="element-42">
                  🎧
                </span>
                <span style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 600,
                color: '#d4a017',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: '0.8rem'
              }} data-id="element-43">
                  Audiobook
                </span>
              </div>
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }} data-id="element-44">
                <span style={{
                fontSize: '1.1rem'
              }} data-id="element-45">
                  ⚡
                </span>
                <span style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 600,
                color: '#d4a017',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: '0.8rem'
              }} data-id="element-46">
                  Instant Download eBook
                </span>
              </div>
            </div>

            {/* Big headline */}
            <h2 style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 900,
            textTransform: 'uppercase',
            lineHeight: 0.95,
            margin: '1.5rem 0 0',
            fontSize: 'clamp(2.8rem, 7vw, 5.5rem)',
            letterSpacing: '-0.02em'
          }} data-id="element-47">
              <span style={{
              color: '#ffffff'
            }} data-id="element-48">
                VOICES OF{' '}
              </span>
              <span style={{
              color: '#c8102e'
            }} data-id="element-49">
                FREEDOM
              </span>
            </h2>

            {/* Red divider */}
            <div style={{
            height: '3px',
            background: 'linear-gradient(to right, transparent, #c8102e, transparent)',
            width: '260px',
            margin: '0.75rem auto'
          }} data-id="element-50" />

            <h3 style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 700,
            color: '#f0f0f0',
            fontSize: 'clamp(1rem, 2.5vw, 1.6rem)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            margin: '0 0 0.4rem'
          }} data-id="element-51">
              The Legacy Of Charlie Kirk
            </h3>

            <p style={{
            fontFamily: "'Merriweather', serif",
            fontStyle: 'italic',
            fontSize: 'clamp(0.8rem, 1.5vw, 1rem)',
            margin: '0 0 1.5rem',
            lineHeight: 1.5
          }} className="font-[500] text-[#E9E9E9]" data-id="element-52">
              The Full Story. Unfiltered. Unforgettable.
            </p>

            {/* ===== INLINE PRICING BLOCK ===== */}
            <div ref={priceRef} style={{
            marginBottom: '1.5rem'
          }} data-id="element-53">
              {/* Price row */}
              <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              marginBottom: '0.75rem',
              flexWrap: 'wrap'
            }} data-id="element-54">
                <span style={{
                fontFamily: "'Oswald', sans-serif",
                color: '#666',
                fontSize: '1.3rem',
                textDecoration: 'line-through'
              }} data-id="element-55">
                  $48
                </span>
                <span style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 900,
                color: '#d4a017',
                fontSize: '3.2rem',
                lineHeight: 1
              }} data-id="element-56">
                  $11.49
                </span>
                <span style={{
                backgroundColor: '#c8102e',
                color: '#ffffff',
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                padding: '0.3rem 0.75rem',
                borderRadius: '999px',
                whiteSpace: 'nowrap',
                alignSelf: 'center'
              }} data-id="element-57">
                  SAVE 76%
                </span>
              </div>

              {/* Feature boxes */}
              <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0.6rem',
              marginBottom: '1.25rem',
              flexWrap: 'wrap'
            }} data-id="element-58">
                {[{
                icon: '🎧',
                label: 'AudioBook'
              }, {
                icon: '📄',
                label: 'PDF Ebook'
              }, {
                icon: '⚡',
                label: 'Instant Access'
              }].map(item => <div key={item.label} style={{
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(200,16,46,0.35)',
                borderRadius: '6px',
                padding: '0.5rem 0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }} data-id="element-59">
                    <span style={{
                  fontSize: '1rem'
                }} data-id="element-60">
                      {item.icon}
                    </span>
                    <span style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 600,
                  color: '#e0e0e0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  fontSize: '0.75rem'
                }} data-id="element-61">
                      {item.label}
                    </span>
                  </div>)}
              </div>

              {/* CTA Button */}
              <motion.button onClick={handleBuyClick} whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.97,
              y: 1
            }} style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              background: 'linear-gradient(180deg, #e8152f 0%, #b50d26 100%)',
              color: '#ffffff',
              border: 'none',
              borderBottom: '4px solid #7a0a1a',
              padding: '1rem 2.5rem',
              fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
              cursor: 'pointer',
              borderRadius: '5px',
              boxShadow: '0 6px 25px rgba(200,16,46,0.5)',
              width: '100%',
              maxWidth: '420px',
              display: 'block',
              margin: '0 auto'
            }} data-id="element-62">
                🔥 Get Your Bundle — $11.49
              </motion.button>
            </div>
            {/* ===== END INLINE PRICING BLOCK ===== */}
          </div>
        </SectionReveal>
      </div>

      {/* ===== AUDIOBOOK SHOWCASE + PRICING ===== */}
      <section style={{
      backgroundColor: '#060e1a',
      padding: '3rem 2rem 2rem',
      textAlign: 'center',
      display: 'none'
    }} data-id="element-63">
        {/* Big audiobook image */}
        <SectionReveal data-id="element-64">
          <img src={AUDIOBOOK_COVER} alt="Voices of Freedom Audiobook" style={{
          width: '100%',
          maxWidth: '480px',
          margin: '0 auto 2.5rem',
          display: 'block',
          borderRadius: '8px',
          boxShadow: '0 30px 100px rgba(200,16,46,0.35), 0 10px 50px rgba(0,0,0,0.9)'
        }} data-id="element-65" />
        </SectionReveal>

        {/* Big headline */}
        <SectionReveal data-id="element-66">
          <h2 style={{
          fontFamily: "'Oswald', sans-serif",
          fontWeight: 900,
          textTransform: 'uppercase',
          lineHeight: 0.95,
          margin: '0 0 0.75rem',
          fontSize: 'clamp(3.5rem, 9vw, 7rem)',
          letterSpacing: '-0.02em'
        }} data-id="element-67">
            <span style={{
            color: '#ffffff'
          }} data-id="element-68">
              VOICES OF{' '}
            </span>
            <span style={{
            color: '#c8102e'
          }} data-id="element-69">
              FREEDOM
            </span>
          </h2>

          <div style={{
          height: '4px',
          background: 'linear-gradient(to right, transparent, #c8102e, transparent)',
          width: '300px',
          margin: '0.75rem auto 1.5rem'
        }} data-id="element-70" />

          <h3 style={{
          fontFamily: "'Oswald', sans-serif",
          fontWeight: 700,
          color: '#ffffff',
          fontSize: 'clamp(1.4rem, 3vw, 2.2rem)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          margin: '0 0 0.75rem'
        }} data-id="element-71">
            The Legacy Of Charlie Kirk
          </h3>

          <p style={{
          fontFamily: "'Merriweather', serif",
          fontStyle: 'italic',
          color: '#aaaaaa',
          fontSize: 'clamp(1rem, 2vw, 1.3rem)',
          margin: '0 0 4rem',
          lineHeight: 1.6
        }} data-id="element-72">
            The Full Story. Unfiltered. Unforgettable.
          </p>
        </SectionReveal>
      </section>

      {/* ===== OPENING HOOK SECTION ===== */}
      <section style={{
      backgroundColor: '#111f35'
    }} className="px-6 md:px-16 py-10 pt-[0px] pb-[0px]" data-id="element-73">
        <div className="max-w-4xl mx-auto" data-id="element-74">
          <SectionReveal data-id="element-75">
            <div style={{
            borderLeft: '4px solid #c8102e',
            paddingLeft: '2rem',
            marginBottom: '2.5rem'
          }} data-id="element-76">
              <p style={{
              fontFamily: "'Merriweather', serif",
              fontStyle: 'italic',
              fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
              lineHeight: 1.7,
              color: '#e8e8e8'
            }} data-id="element-77">
                "They say a single bullet can silence a voice, but they forgot
                that a voice rooted in eternal truth doesn't just fade—it
                becomes an echo that shakes the very foundations of the
                establishment."
              </p>
            </div>
          </SectionReveal>

          <SectionReveal data-id="element-78"></SectionReveal>
        </div>
      </section>

      {/* ===== THE PROBLEM SECTION ===== */}
      <section style={{
      backgroundColor: '#0d1b2e'
    }} className="px-6 md:px-16 py-10 pt-[0px] pb-[0px]" data-id="element-79">
        <div className="max-w-4xl mx-auto" data-id="element-80">
          <SectionReveal data-id="element-81">
            <SectionLabel data-id="element-82">The Problem</SectionLabel>
            <h2 style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 700,
            textTransform: 'uppercase',
            color: '#ffffff',
            lineHeight: 1.1
          }} className="text-4xl md:text-5xl mb-4" data-id="element-83">
              You've Felt the Walls
              <br data-id="element-84" />
              Closing In.
            </h2>
            <RedRule data-id="element-85" />
          </SectionReveal>

          <SectionReveal data-id="element-86">
            <p style={{
            color: '#c8c8c8',
            lineHeight: 1.9,
            marginBottom: '1.5rem',
            fontFamily: "'Merriweather', serif"
          }} className="text-base md:text-lg" data-id="element-87">
              You've seen the classrooms transform from halls of inquiry into
              factories of conformity. You've watched as young men and women—the
              future of our nation—were taught to keep their heads down, afraid
              that one "wrong" word would cost them their grades, their friends,
              or their future.
            </p>
            <p style={{
            color: '#c8c8c8',
            lineHeight: 1.9,
            marginBottom: '2.5rem',
            fontFamily: "'Merriweather', serif"
          }} className="text-base md:text-lg" data-id="element-88">
              The America we inherited is trembling with contradictions. For
              years, Charlie Kirk was the vanguard, the one man standing behind
              the microphone on hostile campuses, giving you the permission to
              breathe, to speak, and to believe again. But after the tragedy
              that shocked the nation, many are asking: Is the fire gone?
            </p>
          </SectionReveal>

          <SectionReveal data-id="element-89">
            <div style={{
            backgroundColor: '#1a2a3e',
            borderLeft: '4px solid #c8102e',
            padding: '1.5rem 2rem',
            borderRadius: '2px'
          }} data-id="element-90">
              <p style={{
              fontFamily: "'Merriweather', serif",
              fontStyle: 'italic',
              fontSize: '1.15rem',
              color: '#e8e8e8',
              lineHeight: 1.7,
              margin: 0
            }} data-id="element-91">
                "We are told to be tolerant of everything—except the faith of
                our fathers and the sovereignty of our borders."
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ===== WHY WHAT YOU TRIED DIDN'T WORK ===== */}

      {/* ===== THE UNIQUE MECHANISM ===== */}
      <section style={{
      backgroundColor: '#0d1b2e'
    }} className="px-6 md:px-16 py-10 pt-[0px] pb-[0px]" data-id="element-92">
        <div className="max-w-4xl mx-auto text-center" data-id="element-93">
          <SectionReveal data-id="element-94">
            <SectionLabel data-id="element-95">The Solution</SectionLabel>
            <h2 style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 700,
            textTransform: 'uppercase',
            color: '#ffffff',
            lineHeight: 1.1
          }} className="text-4xl md:text-6xl mb-4" data-id="element-96">
              The Soldier of
              <br data-id="element-97" />
              <span style={{
              color: '#c8102e'
            }} data-id="element-98">
                Conviction
              </span>{' '}
              Blueprint
            </h2>
            <div style={{
            display: 'flex',
            justifyContent: 'center'
          }} data-id="element-99">
              <RedRule data-id="element-100" />
            </div>
            <h3 style={{
            fontFamily: "'Merriweather', serif",
            fontStyle: 'italic',
            color: '#e8e8e8',
            fontWeight: 400,
            fontSize: '1.2rem',
            marginBottom: '1.5rem'
          }} data-id="element-101">
              This isn't a biography. It's a tactical arsenal.
            </h3>
          </SectionReveal>

          <SectionReveal data-id="element-102">
            <p style={{
            color: '#c8c8c8',
            lineHeight: 1.9,
            fontFamily: "'Merriweather', serif"
          }} className="text-base md:text-lg" data-id="element-103">
              This product is a tactical immersion into the Logic of Defiance.
              We have distilled the "short, sharp sentences" Charlie crafted to
              survive in the age of shrinking attention spans. We reveal how he
              turned cultural arguments into moral ultimatums, rooted not in
              political theory, but in the Bible and the unbreakable laws of
              Western Civilization. You aren't just reading his story—you are
              learning his weapons.
            </p>
          </SectionReveal>

          <SectionReveal data-id="element-104">
            <div style={{
            width: '100%',
            height: '2px',
            backgroundColor: '#c8102e',
            margin: '3rem 0'
          }} data-id="element-105" />
          </SectionReveal>
        </div>
      </section>

      {/* ===== WHAT YOU GET ===== */}
      <section style={{
      backgroundColor: '#111f35'
    }} className="px-6 md:px-16 py-10" data-id="element-106">
        <div className="max-w-5xl mx-auto" data-id="element-107">
          <SectionReveal data-id="element-108">
            <SectionLabel data-id="element-109">What You Get</SectionLabel>
            <h2 style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 700,
            textTransform: 'uppercase',
            color: '#ffffff',
            lineHeight: 1.1
          }} className="text-4xl md:text-5xl mb-4" data-id="element-110">
              The Complete Voices of Freedom
              <br data-id="element-111" />
              Legacy Bundle
            </h2>
            <RedRule data-id="element-112" />
          </SectionReveal>

          {/* Product images */}
          <SectionReveal data-id="element-113">
            <div className="flex flex-col md:flex-row justify-center items-end my-12 gap-[32px]" data-id="element-114">
              <div className="text-center" data-id="element-115">
                <img src={BOOK_COVER} alt="Voices of Freedom eBook cover" style={{
                maxWidth: '220px',
                width: '100%',
                borderRadius: '4px',
                boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                margin: '0 auto',
                display: 'block'
              }} data-id="element-116" />
                <p style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 600,
                color: '#d4a017',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginTop: '0.75rem',
                fontSize: '0.9rem'
              }} data-id="element-117">
                  eBook — 143 Pages
                </p>
              </div>
              <div className="text-center" data-id="element-118">
                <img src={AUDIOBOOK_COVER} alt="Voices of Freedom Audiobook cover" style={{
                maxWidth: '220px',
                width: '100%',
                borderRadius: '4px',
                boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                margin: '0 auto',
                display: 'block'
              }} data-id="element-119" />
                <p style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 600,
                color: '#d4a017',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginTop: '0.75rem',
                fontSize: '0.9rem'
              }} data-id="element-120">
                  Audiobook — Full Narration 1hr 43 mins
                </p>
              </div>
            </div>
          </SectionReveal>

          {/* Bullet items */}
          <div className="space-y-5" data-id="element-121">
            <SectionReveal data-id="element-122">
              <div className="flex items-start gap-4" data-id="element-123">
                <span style={{
                color: '#c8102e',
                fontSize: '1.4rem',
                fontWeight: 700,
                lineHeight: 1.2,
                flexShrink: 0
              }} data-id="element-124">
                  ✓
                </span>
                <div data-id="element-125">
                  <p style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 700,
                  color: '#ffffff',
                  fontSize: '1.1rem',
                  textTransform: 'uppercase',
                  marginBottom: '0.25rem'
                }} data-id="element-126">
                    The Full "Voices of Freedom" eBook — 143 Pages
                  </p>
                  <p style={{
                  fontFamily: "'Merriweather', serif",
                  color: '#b0b8c8',
                  lineHeight: 1.7,
                  fontSize: '0.9rem',
                  margin: 0
                }} data-id="element-127">
                    Raw, unapologetic narrative covering his rise from an
                    18-year-old founder to a national martyr.
                  </p>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal data-id="element-128">
              <div className="flex items-start gap-4" data-id="element-129">
                <span style={{
                color: '#c8102e',
                fontSize: '1.4rem',
                fontWeight: 700,
                lineHeight: 1.2,
                flexShrink: 0
              }} data-id="element-130">
                  ✓
                </span>
                <div data-id="element-131">
                  <p style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 700,
                  color: '#ffffff',
                  fontSize: '1.1rem',
                  textTransform: 'uppercase',
                  marginBottom: '0.25rem'
                }} data-id="element-132">
                    The "Voices of Freedom" Audiobook 1hr 43mins
                  </p>
                  <p style={{
                  fontFamily: "'Merriweather', serif",
                  color: '#b0b8c8',
                  lineHeight: 1.7,
                  fontSize: '0.9rem',
                  margin: 0
                }} data-id="element-133">
                    Narrated with the urgency and fire the subject
                    deserves—perfect for the patriot on the move.
                  </p>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal data-id="element-134">
              <div className="flex items-start gap-4" data-id="element-135">
                <span style={{
                color: '#c8102e',
                fontSize: '1.4rem',
                fontWeight: 700,
                lineHeight: 1.2,
                flexShrink: 0
              }} data-id="element-136">
                  ✓
                </span>
                <div data-id="element-137">
                  <p style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 700,
                  color: '#ffffff',
                  fontSize: '1.1rem',
                  textTransform: 'uppercase',
                  marginBottom: '0.25rem'
                }} data-id="element-138">
                    The Battlefield Vocabulary — Brevity as a Weapon
                  </p>
                  <p style={{
                  fontFamily: "'Merriweather', serif",
                  color: '#b0b8c8',
                  lineHeight: 1.7,
                  fontSize: '0.9rem',
                  margin: 0
                }} data-id="element-139">
                    Learn the "Brevity as a Weapon" technique Charlie used to
                    dismantle professors twice his age.
                  </p>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal data-id="element-140">
              <div className="flex items-start gap-4" data-id="element-141">
                <span style={{
                color: '#c8102e',
                fontSize: '1.4rem',
                fontWeight: 700,
                lineHeight: 1.2,
                flexShrink: 0
              }} data-id="element-142">
                  ✓
                </span>
                <div data-id="element-143">
                  <p style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 700,
                  color: '#ffffff',
                  fontSize: '1.1rem',
                  textTransform: 'uppercase',
                  marginBottom: '0.25rem'
                }} data-id="element-144">
                    The Faith & Freedom Connection — Chapter XI Deep Dive
                  </p>
                  <p style={{
                  fontFamily: "'Merriweather', serif",
                  color: '#b0b8c8',
                  lineHeight: 1.7,
                  fontSize: '0.9rem',
                  margin: 0
                }} data-id="element-145">
                    Why liberty is a gift from God, not a grant from the
                    government. The spiritual foundation that anchored
                    everything.
                  </p>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal data-id="element-146">
              <div className="flex items-start gap-4" data-id="element-147">
                <span style={{
                color: '#c8102e',
                fontSize: '1.4rem',
                fontWeight: 700,
                lineHeight: 1.2,
                flexShrink: 0
              }} data-id="element-148">
                  ✓
                </span>
                <div data-id="element-149">
                  <p style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 700,
                  color: '#ffffff',
                  fontSize: '1.1rem',
                  textTransform: 'uppercase',
                  marginBottom: '0.25rem'
                }} data-id="element-150">
                    The Post-2024 Analysis — The Assassination & Its Aftermath
                  </p>
                  <p style={{
                  fontFamily: "'Merriweather', serif",
                  color: '#b0b8c8',
                  lineHeight: 1.7,
                  fontSize: '0.9rem',
                  margin: 0
                }} data-id="element-151">
                    A sobering, honest look at the event that shook the world
                    and why his flame now burns brighter in death.
                  </p>
                </div>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* ===== WHO THIS IS FOR / NOT FOR ===== */}
      <section style={{
      backgroundColor: '#0d1b2e'
    }} className="px-6 md:px-16 py-10" data-id="element-152">
        <div className="max-w-5xl mx-auto" data-id="element-153">
          <SectionReveal data-id="element-154">
            <div className="text-center mb-12" data-id="element-155">
              <SectionLabel data-id="element-156">Know Your Place in This Fight</SectionLabel>
              <h2 style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              textTransform: 'uppercase',
              color: '#ffffff',
              lineHeight: 1.1
            }} className="text-4xl md:text-5xl" data-id="element-157">
                Is This For You?
              </h2>
            </div>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-158">
            {/* For */}
            <SectionReveal data-id="element-159"></SectionReveal>

            {/* Not For */}
            <SectionReveal data-id="element-160">
              <div style={{
              backgroundColor: '#1a1a2e',
              border: '1px solid rgba(255,255,255,0.08)',
              padding: '2rem',
              borderRadius: '2px'
            }} data-id="element-161">
                <h3 style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#f87171',
                fontSize: '1.2rem',
                letterSpacing: '0.15em',
                marginBottom: '1.5rem',
                borderBottom: '1px solid rgba(248,113,113,0.2)',
                paddingBottom: '0.75rem'
              }} data-id="element-162">
                  ✗ This Is NOT For You If...
                </h3>
                <div className="space-y-4" data-id="element-163">
                  <div className="flex items-start gap-3" data-id="element-164">
                    <span style={{
                    color: '#f87171',
                    flexShrink: 0,
                    marginTop: '2px'
                  }} data-id="element-165">
                      ✗
                    </span>
                    <p style={{
                    fontFamily: "'Merriweather', serif",
                    color: '#888',
                    fontSize: '0.9rem',
                    lineHeight: 1.7,
                    margin: 0
                  }} data-id="element-166">
                      You value "fitting in" over standing up.
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-167">
                    <span style={{
                    color: '#f87171',
                    flexShrink: 0,
                    marginTop: '2px'
                  }} data-id="element-168">
                      ✗
                    </span>
                    <p style={{
                    fontFamily: "'Merriweather', serif",
                    color: '#888',
                    fontSize: '0.9rem',
                    lineHeight: 1.7,
                    margin: 0
                  }} data-id="element-169">
                      You believe the government should be the ultimate moral
                      authority.
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-170">
                    <span style={{
                    color: '#f87171',
                    flexShrink: 0,
                    marginTop: '2px'
                  }} data-id="element-171">
                      ✗
                    </span>
                    <div data-id="element-172">
                      <p style={{
                      fontFamily: "'Oswald', sans-serif",
                      fontWeight: 600,
                      color: '#888',
                      textTransform: 'uppercase',
                      fontSize: '0.95rem',
                      marginBottom: '0.2rem'
                    }} data-id="element-173">
                        The "Apolitical"
                      </p>
                      <p style={{
                      fontFamily: "'Merriweather', serif",
                      color: '#888',
                      fontSize: '0.85rem',
                      lineHeight: 1.7,
                      margin: 0
                    }} data-id="element-174">
                        In a war for the future, there is no neutral ground.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section style={{
      backgroundColor: '#111f35'
    }} className="px-6 md:px-16 py-10" data-id="element-175">
        <div className="max-w-5xl mx-auto" data-id="element-176">
          <SectionReveal data-id="element-177">
            <div className="text-center mb-14" data-id="element-178">
              <SectionLabel data-id="element-179">How It Works</SectionLabel>
              <h2 style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              textTransform: 'uppercase',
              color: '#ffffff',
              lineHeight: 1.1
            }} className="text-4xl md:text-5xl" data-id="element-180">
                Three Steps to
                <br data-id="element-181" />
                Reclaim Your Voice
              </h2>
            </div>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-182">
            <SectionReveal data-id="element-183">
              <div className="text-center md:text-left" data-id="element-184">
                <p style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                color: '#c8102e',
                fontSize: '5rem',
                lineHeight: 1,
                marginBottom: '0.5rem'
              }} data-id="element-185">
                  01
                </p>
                <h3 style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#ffffff',
                fontSize: '1.3rem',
                marginBottom: '0.75rem'
              }} data-id="element-186">
                  Instant Digital Delivery
                </h3>
                <p style={{
                fontFamily: "'Merriweather', serif",
                color: '#b0b8c8',
                lineHeight: 1.8,
                fontSize: '0.9rem',
                margin: 0
              }} data-id="element-187">
                  Secure the PDF and Audiobook immediately. No waiting for the
                  mail while the world moves on.
                </p>
              </div>
            </SectionReveal>

            <SectionReveal data-id="element-188">
              <div className="text-center md:text-left" data-id="element-189">
                <p style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                color: '#c8102e',
                fontSize: '5rem',
                lineHeight: 1,
                marginBottom: '0.5rem'
              }} data-id="element-190">
                  02
                </p>
                <h3 style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#ffffff',
                fontSize: '1.3rem',
                marginBottom: '0.75rem'
              }} data-id="element-191">
                  The 7-Day Awakening
                </h3>
                <p style={{
                fontFamily: "'Merriweather', serif",
                color: '#b0b8c8',
                lineHeight: 1.8,
                fontSize: '0.9rem',
                margin: 0
              }} data-id="element-192">
                  Read the first five chapters and feel the "permission to
                  speak" return to your daily life.
                </p>
              </div>
            </SectionReveal>

            <SectionReveal data-id="element-193">
              <div className="text-center md:text-left" data-id="element-194">
                <p style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                color: '#c8102e',
                fontSize: '5rem',
                lineHeight: 1,
                marginBottom: '0.5rem'
              }} data-id="element-195">
                  03
                </p>
                <h3 style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#ffffff',
                fontSize: '1.3rem',
                marginBottom: '0.75rem'
              }} data-id="element-196">
                  The Legacy Carry
                </h3>
                <p style={{
                fontFamily: "'Merriweather', serif",
                color: '#b0b8c8',
                lineHeight: 1.8,
                fontSize: '0.9rem',
                margin: 0
              }} data-id="element-197">
                  Use the distilled arguments and faith-based frameworks to
                  become a "Microphone" in your own sphere of influence.
                </p>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* ===== PROOF / BELIEVABILITY ===== */}
      <section style={{
      backgroundColor: '#0d1b2e'
    }} className="px-6 md:px-16 py-10" data-id="element-198">
        <div className="max-w-4xl mx-auto text-center" data-id="element-199">
          <SectionReveal data-id="element-200">
            <SectionLabel data-id="element-201">The Proof</SectionLabel>
            <div style={{
            borderTop: '2px solid #c8102e',
            borderBottom: '2px solid #c8102e',
            padding: '2.5rem 0',
            margin: '1.5rem 0'
          }} data-id="element-202">
              <p style={{
              fontFamily: "'Merriweather', serif",
              fontStyle: 'italic',
              fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
              lineHeight: 1.8,
              color: '#e8e8e8',
              margin: 0
            }} data-id="element-203">
                "Charlie Kirk started Turning Point USA at eighteen with a
                folding table. By the time he was taken from us, he had built a
                Student Army that reached every corner of the Union."
              </p>
            </div>
            <p style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 600,
            color: '#d4a017',
            fontSize: '1.2rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }} data-id="element-204">
              His life is the proof: One voice, backed by God and conviction,
              can move mountains.
            </p>
            <p style={{
            fontFamily: "'Merriweather', serif",
            color: '#888',
            fontSize: '0.9rem',
            marginTop: '0.5rem'
          }} data-id="element-205">
              This book shows you how he did it.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* ===== FAQ SECTION ===== */}
      <section style={{
      backgroundColor: '#111f35'
    }} className="px-6 md:px-16 py-10" data-id="element-206">
        <div className="max-w-3xl mx-auto" data-id="element-207">
          <SectionReveal data-id="element-208">
            <div className="text-center mb-12" data-id="element-209">
              <SectionLabel data-id="element-210">Frequently Asked Questions</SectionLabel>
              <h2 style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              textTransform: 'uppercase',
              color: '#ffffff',
              lineHeight: 1.1
            }} className="text-4xl md:text-5xl" data-id="element-211">
                Your Questions,
                <br data-id="element-212" />
                Answered
              </h2>
            </div>
          </SectionReveal>

          <div className="space-y-3" data-id="element-213">
            {faqs.map((faq, index) => <SectionReveal key={index} data-id="element-214">
                <div style={{
              backgroundColor: '#1a2a3e',
              border: '1px solid rgba(200,16,46,0.2)',
              borderRadius: '2px',
              overflow: 'hidden'
            }} data-id="element-215">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1.25rem 1.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left'
              }} aria-expanded={openFaq === index} data-id="element-216">
                    <span style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 600,
                  color: '#ffffff',
                  fontSize: '1.05rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }} data-id="element-217">
                      {faq.q}
                    </span>
                    <span style={{
                  color: '#c8102e',
                  flexShrink: 0,
                  marginLeft: '1rem'
                }} data-id="element-218">
                      {openFaq === index ? <ChevronUpIcon size={20} data-id="element-219" /> : <ChevronDownIcon size={20} data-id="element-220" />}
                    </span>
                  </button>

                  <AnimatePresence data-id="element-221">
                    {openFaq === index && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} style={{
                  overflow: 'hidden'
                }} data-id="element-222">
                        <div style={{
                    padding: '0 1.5rem 1.5rem',
                    borderTop: '1px solid rgba(200,16,46,0.15)'
                  }} data-id="element-223">
                          <p style={{
                      fontFamily: "'Merriweather', serif",
                      color: '#b0b8c8',
                      lineHeight: 1.8,
                      fontSize: '0.9rem',
                      margin: '1rem 0 0'
                    }} data-id="element-224">
                            {faq.a}
                          </p>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </SectionReveal>)}
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer style={{
      backgroundColor: '#060e1a',
      borderTop: '2px solid #c8102e',
      padding: '3rem 2rem',
      textAlign: 'center'
    }} data-id="element-225">
        <div className="max-w-4xl mx-auto" data-id="element-226">
          <p style={{
          fontFamily: "'Oswald', sans-serif",
          fontWeight: 700,
          textTransform: 'uppercase',
          color: '#ffffff',
          fontSize: '1.1rem',
          letterSpacing: '0.1em',
          marginBottom: '0.75rem'
        }} data-id="element-227">
            Stand with the legacy. Amplify the echo.
          </p>
          <div style={{
          width: '3rem',
          height: '2px',
          backgroundColor: '#c8102e',
          margin: '0 auto 1.5rem'
        }} data-id="element-228" />
          <p style={{
          fontFamily: "'Merriweather', serif",
          color: '#555',
          fontSize: '0.75rem',
          lineHeight: 1.7,
          marginBottom: '1.25rem'
        }} data-id="element-229">
            © 2025 Voices of Freedom. All rights reserved. This product is for
            informational and educational purposes only. Digital delivery
            license is for a single household. Results may vary. This page is
            not affiliated with any political party or government entity.
          </p>
          {/* Footer legal links */}
          <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '2rem',
          flexWrap: 'wrap'
        }} data-id="element-230">
            <button onClick={() => setActiveModal('privacy')} style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontSize: '0.75rem',
            color: '#888',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            transition: 'color 0.2s'
          }} onMouseEnter={e => e.currentTarget.style.color = '#c8102e'} onMouseLeave={e => e.currentTarget.style.color = '#888'} data-id="element-231">
              Privacy Policy
            </button>
            <span style={{
            color: '#333',
            fontSize: '0.75rem',
            lineHeight: '1.5'
          }} data-id="element-232">
              |
            </span>
            <button onClick={() => setActiveModal('terms')} style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontSize: '0.75rem',
            color: '#888',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            transition: 'color 0.2s'
          }} onMouseEnter={e => e.currentTarget.style.color = '#c8102e'} onMouseLeave={e => e.currentTarget.style.color = '#888'} data-id="element-233">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      {/* ===== LEGAL MODAL ===== */}
      <AnimatePresence data-id="element-234">
        {activeModal && <motion.div key="modal-backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} onClick={() => setActiveModal(null)} style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }} data-id="element-235">
            <motion.div key="modal-panel" initial={{
          opacity: 0,
          y: 30,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} style={{
          backgroundColor: '#0d1b2e',
          border: '1px solid rgba(200,16,46,0.4)',
          borderTop: '3px solid #c8102e',
          borderRadius: '4px',
          width: '100%',
          maxWidth: '680px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 30px 80px rgba(0,0,0,0.9)'
        }} data-id="element-236">
              {/* Modal header */}
              <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1.25rem 1.75rem',
            borderBottom: '1px solid rgba(200,16,46,0.2)',
            flexShrink: 0
          }} data-id="element-237">
                <div data-id="element-238">
                  <p style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 700,
                color: '#c8102e',
                textTransform: 'uppercase',
                letterSpacing: '0.2em',
                fontSize: '0.7rem',
                margin: '0 0 0.2rem'
              }} data-id="element-239">
                    Voices of Freedom
                  </p>
                  <h2 style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 900,
                color: '#ffffff',
                textTransform: 'uppercase',
                fontSize: '1.4rem',
                letterSpacing: '0.04em',
                margin: 0
              }} data-id="element-240">
                    {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                  </h2>
                </div>
                <button onClick={() => setActiveModal(null)} style={{
              background: 'rgba(200,16,46,0.1)',
              border: '1px solid rgba(200,16,46,0.3)',
              borderRadius: '4px',
              color: '#c8102e',
              cursor: 'pointer',
              padding: '0.4rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'background 0.2s'
            }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(200,16,46,0.25)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(200,16,46,0.1)'} aria-label="Close" data-id="element-241">
                  <XIcon size={18} data-id="element-242" />
                </button>
              </div>
              {/* Modal scrollable body */}
              <div style={{
            overflowY: 'auto',
            padding: '1.75rem',
            flex: 1
          }} data-id="element-243">
                {activeModal === 'privacy' ? privacyContent : termsContent}
              </div>
              {/* Modal footer */}
              <div style={{
            padding: '1rem 1.75rem',
            borderTop: '1px solid rgba(200,16,46,0.15)',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end'
          }} data-id="element-244">
                <button onClick={() => setActiveModal(null)} style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontSize: '0.85rem',
              backgroundColor: '#c8102e',
              color: '#ffffff',
              border: 'none',
              borderBottom: '3px solid #7a0a1a',
              padding: '0.6rem 1.5rem',
              cursor: 'pointer',
              borderRadius: '3px'
            }} data-id="element-245">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* ===== STICKY MOBILE CTA BAR ===== */}
      <AnimatePresence data-id="element-246">
        {showStickyBar && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#0d1b2e',
        borderTop: '2px solid #c8102e',
        padding: '0.75rem 1rem',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem'
      }} className="md:hidden" data-id="element-247">
            <div data-id="element-248">
              <p style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 700,
            color: '#ffffff',
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            margin: 0
          }} data-id="element-249">
                Legacy Bundle
              </p>
              <p style={{
            fontFamily: "'Oswald', sans-serif",
            color: '#d4a017',
            fontWeight: 700,
            fontSize: '1.2rem',
            margin: 0
          }} data-id="element-250">
                $11.49
              </p>
            </div>
            <button onClick={scrollToPrice} style={{
          fontFamily: "'Oswald', sans-serif",
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          backgroundColor: '#c8102e',
          color: '#ffffff',
          border: '2px solid #d4a017',
          padding: '0.6rem 1.25rem',
          fontSize: '0.85rem',
          cursor: 'pointer',
          flexShrink: 0
        }} data-id="element-251">
              CLAIM NOW
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}